import React, { useState, useRef, useCallback } from 'react'
import { Camera, Upload, X, RotateCcw, Check } from 'lucide-react'

interface PhotoUploadModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (file: File) => Promise<void>
  choreName: string
  isRequired: boolean
}

export default function PhotoUploadModal({
  isOpen,
  onClose,
  onSubmit,
  choreName,
  isRequired
}: PhotoUploadModalProps) {
  const [capturedImage, setCapturedImage] = useState<string | null>(null)
  const [capturedFile, setCapturedFile] = useState<File | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showCamera, setShowCamera] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const streamRef = useRef<MediaStream | null>(null)

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    setShowCamera(false)
  }, [])

  const startCamera = async () => {
    try {
      setError(null)
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
      }
      setShowCamera(true)
    } catch (err) {
      console.error('Camera access error:', err)
      setError('Unable to access camera. Please use file upload instead.')
    }
  }

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return

    const video = videoRef.current
    const canvas = canvasRef.current
    const context = canvas.getContext('2d')

    if (!context) return

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    context.drawImage(video, 0, 0)

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `chore-photo-${Date.now()}.jpg`, { type: 'image/jpeg' })
        setCapturedFile(file)
        setCapturedImage(canvas.toDataURL('image/jpeg'))
        stopCamera()
      }
    }, 'image/jpeg', 0.8)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file')
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('Image must be less than 10MB')
        return
      }
      setError(null)
      setCapturedFile(file)
      const reader = new FileReader()
      reader.onload = (e) => {
        setCapturedImage(e.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRetake = () => {
    setCapturedImage(null)
    setCapturedFile(null)
    setError(null)
  }

  const handleSubmit = async () => {
    if (!capturedFile) {
      if (!isRequired) {
        onClose()
        return
      }
      setError('Please take or upload a photo')
      return
    }

    setIsSubmitting(true)
    setError(null)
    try {
      await onSubmit(capturedFile)
      handleClose()
    } catch (err: any) {
      setError(err.message || 'Failed to upload photo')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    stopCamera()
    setCapturedImage(null)
    setCapturedFile(null)
    setError(null)
    onClose()
  }

  const handleSkip = () => {
    if (!isRequired) {
      onSubmit(null as any)
      handleClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {isRequired ? 'Photo Required' : 'Add Photo (Optional)'}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <p className="text-sm text-gray-600">
            {isRequired
              ? `Take a photo to prove you completed "${choreName}"`
              : `Optionally add a photo of your completed "${choreName}"`}
          </p>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Camera/Preview Area */}
          <div className="relative aspect-[4/3] bg-gray-100 rounded-lg overflow-hidden">
            {showCamera && !capturedImage ? (
              <>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={capturePhoto}
                  className="absolute bottom-4 left-1/2 -translate-x-1/2 w-16 h-16 bg-white rounded-full border-4 border-blue-500 flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
                >
                  <div className="w-12 h-12 bg-blue-500 rounded-full" />
                </button>
                <button
                  onClick={stopCamera}
                  className="absolute top-2 right-2 p-2 bg-black/50 rounded-full text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </>
            ) : capturedImage ? (
              <>
                <img
                  src={capturedImage}
                  alt="Captured"
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={handleRetake}
                  className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-white rounded-lg shadow-lg flex items-center gap-2 hover:bg-gray-50 transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Retake
                </button>
              </>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-4 p-4">
                <Camera className="w-16 h-16 text-gray-300" />
                <p className="text-gray-500 text-center">
                  Take a photo or upload from your device
                </p>
              </div>
            )}
          </div>

          {/* Hidden canvas for capturing */}
          <canvas ref={canvasRef} className="hidden" />

          {/* Action Buttons */}
          {!capturedImage && !showCamera && (
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={startCamera}
                className="flex items-center justify-center gap-2 py-3 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                <Camera className="w-5 h-5" />
                Take Photo
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center justify-center gap-2 py-3 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <Upload className="w-5 h-5" />
                Upload
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          )}

          {/* Submit/Skip Buttons */}
          <div className="flex gap-3 pt-2">
            {!isRequired && !capturedImage && (
              <button
                onClick={handleSkip}
                className="flex-1 py-3 px-4 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Skip Photo
              </button>
            )}
            {capturedImage && (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Submit Photo
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
